"use client";

import { useIsTouchDevice } from "@dc/hooks";
import type { Tooltip as HeroTooltip } from "@heroui/react";
import type { CSSProperties, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Tooltip } from "../Tooltip";

type TooltipProps = React.ComponentProps<typeof HeroTooltip>;

export interface TouchableTooltipProps extends Omit<TooltipProps, "isOpen" | "onOpenChange"> {
  /** The content to display inside the tooltip */
  content: ReactNode;
  /** The element that triggers the tooltip */
  children: ReactNode;
  /** Whether to enable tap-to-toggle on touch devices. Defaults to true. */
  enableTouchTap?: boolean;
  /** Minimum tap target size using Tailwind spacing scale (e.g., 10 = 2.5rem = 40px). Recommended: 10-12 for accessibility. */
  tapTargetSize?: number;
  /** Accessible label for the trigger button. Required when children is an icon-only element. */
  ariaLabel?: string;
}

/**
 * A tooltip component that supports tap-to-show on touch devices.
 * On touch devices, tapping the trigger element toggles the tooltip visibility.
 * On non-touch devices, the tooltip appears on hover as usual.
 *
 * @example
 * <TouchableTooltip content="Helpful information">
 *   <InfoCircleLinear />
 * </TouchableTooltip>
 *
 * @example
 * // Icon-only trigger: ariaLabel names it and makes it keyboard focusable
 * <TouchableTooltip ariaLabel="What this covers" content="Helpful information">
 *   <InfoCircleLinear />
 * </TouchableTooltip>
 *
 * @example
 * // With larger tap target for accessibility (size-10 = 2.5rem = 40px)
 * <TouchableTooltip content="Helpful information" tapTargetSize={10}>
 *   <InfoCircleLinear />
 * </TouchableTooltip>
 */
export const TouchableTooltip = ({
  content,
  children,
  enableTouchTap = true,
  tapTargetSize,
  ariaLabel,
  ...props
}: TouchableTooltipProps) => {
  const isTouchDevice = useIsTouchDevice();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handlePress = useCallback(() => {
    if (isTouchDevice && enableTouchTap) {
      setIsOpen((prev) => !prev);
    }
  }, [isTouchDevice, enableTouchTap]);

  // Handle internal tooltip close events (Escape key, etc.)
  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
  }, []);

  // Close tooltip when clicking outside (for touch devices)
  useEffect(() => {
    if (!isTouchDevice || !enableTouchTap || !isOpen) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    // Use a small delay to avoid the current click from immediately closing the tooltip
    const timeoutId = setTimeout(() => {
      document.addEventListener("click", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isTouchDevice, enableTouchTap, isOpen]);

  // On touch devices with tap enabled, use controlled mode
  if (isTouchDevice && enableTouchTap) {
    // Convert Tailwind spacing scale to rem (e.g., 10 = 2.5rem = 40px)
    const sizeInRem = tapTargetSize ? tapTargetSize * 0.25 : 0;
    // Assuming icon is size-4 (1rem), calculate offset to maintain visual spacing
    const tapTargetOffset = tapTargetSize ? (sizeInRem - 1) / 2 : 0;

    const tapTargetStyles: CSSProperties = {
      background: "none",
      border: "none",
      padding: 0,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      ...(tapTargetSize && {
        minWidth: `${sizeInRem}rem`,
        minHeight: `${sizeInRem}rem`,
        margin: `-${tapTargetOffset}rem`,
      }),
    };

    // Use explicit ariaLabel, or derive from content if it's a string
    const accessibleLabel = ariaLabel ?? (typeof content === "string" ? content : undefined);

    return (
      <Tooltip content={content} isOpen={isOpen} onOpenChange={handleOpenChange} {...props}>
        <button
          ref={buttonRef}
          type="button"
          onClick={handlePress}
          style={tapTargetStyles}
          aria-label={accessibleLabel}
        >
          {children}
        </button>
      </Tooltip>
    );
  }

  // Hover alone strands keyboard users, so a labelled trigger gets a focusable wrapper.
  // Without ariaLabel the child is assumed to carry its own trigger semantics.
  return (
    <Tooltip content={content} {...props}>
      {ariaLabel ? (
        <button aria-label={ariaLabel} className="inline-flex items-center" type="button">
          {children}
        </button>
      ) : (
        children
      )}
    </Tooltip>
  );
};
